import React, { useCallback, useEffect, useState } from 'react';
import { apiCall } from '../../utils/api';
import { Landmark, Loader2 } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';

const RANGES = [30, 90, 180];

export default function SettlementAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await apiCall(`/api/admin/finance/settlements/analytics?days=${days}`));
    } catch { /* keep last */ }
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const sum = data?.summary || {};
  const series = data?.series || [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="settlement-analytics">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
          <Landmark className="w-4 h-4 text-indigo-600" /> Settlement Trends
        </h4>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button key={r} onClick={() => setDays(r)}
              className={`px-2.5 py-1 rounded-lg text-xs font-bold ${days === r ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
              data-testid={`settlement-analytics-range-${r}`}>
              {r}d
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
        {[
          ['Settlements', sum.total ?? 0, 'settlement-kpi-total'],
          ['Volume', `$${Number(sum.volume || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, 'settlement-kpi-volume'],
          ['Hold rate', `${sum.hold_rate_pct ?? 0}%`, 'settlement-kpi-holdrate'],
          ['Flag rate', `${sum.flagged_rate_pct ?? 0}%`, 'settlement-kpi-flagrate'],
          ['Avg risk', sum.avg_fraud ?? 0, 'settlement-kpi-avgrisk'],
        ].map(([label, value, tid]) => (
          <div key={tid} className="bg-slate-50 rounded-lg p-2.5 text-center" data-testid={tid}>
            <p className="text-base font-bold text-gray-900">{value}</p>
            <p className="text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
          </div>
        ))}
      </div>
      {loading && !data ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div>
      ) : series.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-8" data-testid="settlement-analytics-empty">No settlements in this window yet.</p>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={series} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
            <YAxis yAxisId="vol" tick={{ fontSize: 10 }} />
            <YAxis yAxisId="risk" orientation="right" domain={[0, 100]} tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v, name) => (name === 'Volume ($)' ? `$${Number(v).toFixed(2)}` : v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="vol" dataKey="volume" name="Volume ($)" fill="#6366f1" radius={[3, 3, 0, 0]} />
            <Bar yAxisId="vol" dataKey="held" name="Held" fill="#f59e0b" radius={[3, 3, 0, 0]} />
            <Line yAxisId="risk" type="monotone" dataKey="avg_fraud" name="Avg fraud score" stroke="#e11d48" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
